import React from "react";
import { Link, NavLink } from "react-router-dom";
import { isAllowed } from "helper/acl";
import { getDocumentLibraryGroups } from "helper/documentModules";
import { compose } from "helper/compose";
import { withTranslation, type WithTranslation } from "react-i18next";
import { withModulesData, type ModulesQueryProps } from "api/queries/useModules";

interface Library {
  slug: string;
  routePath?: string;
  externalUrl?: string | null;
  title?: string;
  titleKey?: string;
  frontPermission?: string;
}

interface LibraryGroup {
  titleKey: string;
  libraries: Library[];
}

interface NavigationOwnProps extends WithTranslation {
  location: { pathname: string };
  customerData: Record<string, unknown>;
  userData?: Record<string, unknown>;
}

type NavigationProps = NavigationOwnProps & ModulesQueryProps;

function Navigation({ t, location, modules }: NavigationProps) {
  const [openDropdownKey, setOpenDropdownKey] = React.useState<string | null>(null);

  const openDropdown = (groupKey: string) => {
    setOpenDropdownKey((prev) => (prev !== groupKey ? groupKey : prev));
  };

  const closeDropdown = () => {
    setOpenDropdownKey((prev) => (prev !== null ? null : prev));
  };

  const handleDropdownItemClick = () => {
    closeDropdown();
  };

  const handleNavigationKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      closeDropdown();
    }
  };

  const isLibraryGroupActive = (group: LibraryGroup) => {
    return group.libraries.some(
      (library) =>
        library.routePath &&
        location.pathname.search(library.routePath.replace(BASE_PATH, "")) !== -1
    );
  };

  const getVisibleModuleGroups = (): LibraryGroup[] => {
    return (getDocumentLibraryGroups(modules) as LibraryGroup[])
      .map((group) => ({
        ...group,
        libraries: group.libraries.filter(
          (library) => library && library.routePath && isAllowed(library.frontPermission)
        ),
      }))
      .filter((group) => group.libraries.length > 0);
  };

  const renderLibrarySubmenu = (group: LibraryGroup) => {
    return (
      <div
        className="Navigation-dropdownMenu"
        role="menu"
        aria-label={t(group.titleKey)}
        hidden={openDropdownKey !== group.titleKey}
        tabIndex={-1}
        onKeyDown={handleNavigationKeyDown}
      >
        {group.libraries.map((library) => {
          const label = library.title || t(library.titleKey || "");

          if (library.externalUrl) {
            return (
              <a
                key={library.slug}
                href={library.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="Navigation-dropdownLink"
                role="menuitem"
                onClick={handleDropdownItemClick}
              >
                {label}
              </a>
            );
          }

          return (
            <Link
              key={library.slug}
              to={library.routePath || "#"}
              className="Navigation-dropdownLink"
              role="menuitem"
              onClick={handleDropdownItemClick}
            >
              {label}
            </Link>
          );
        })}
      </div>
    );
  };

  const renderLibraryDropdown = (group: LibraryGroup) => {
    const isActive = isLibraryGroupActive(group);
    const firstLibrary = group.libraries[0];
    const linkTarget = firstLibrary?.routePath || "#";
    const label = t(group.titleKey);

    const isOpen = openDropdownKey === group.titleKey;

    return (
      <div
        className={
          "Navigation-dropdown " + (isActive ? "isActive" : "") + (isOpen ? " isOpen" : "")
        }
        key={group.titleKey}
        onMouseEnter={() => openDropdown(group.titleKey)}
        onMouseLeave={closeDropdown}
      >
        <Link
          to={linkTarget}
          className={"Navigation-entry Navigation-entry--dropdown " + (isActive ? "isActive" : "")}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={handleDropdownItemClick}
          onKeyDown={handleNavigationKeyDown}
        >
          <span className="Navigation-entryName">{label}</span>
          <span className="Navigation-entryArrow" aria-hidden="true"></span>
        </Link>
        {renderLibrarySubmenu(group)}
      </div>
    );
  };

  const moduleGroups = getVisibleModuleGroups();
  const otherModuleGroups = moduleGroups.filter(
    (group) => group.titleKey !== "navigation.modules.dashboard"
  );

  return (
    <nav className="Navigation Navigation--top" aria-label={t("navigation.label_dashboard")}>
      <NavLink
        to={BASE_PATH + "/"}
        end
        className={({ isActive }) => "Navigation-entry isHome" + (isActive ? " isActive" : "")}
      >
        <span className="Navigation-entryName">{t("navigation.label_dashboard")}</span>
      </NavLink>

      {otherModuleGroups.map((group) => renderLibraryDropdown(group))}
    </nav>
  );
}

export default compose(withTranslation(), withModulesData)(Navigation);
